
/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */

import endpoints from "constants/api/Endpoints";
import EndpointProps from "interface/EndpointProps";
import SignUpRequest from "constants/api/models/SignUpRequest";
import { SubscriptionSetupData } from "constants/api/models/SignUpResponse";
import { logError } from "services/logService/errorLogger";
import { friendlyLoginError, friendlySignUpError } from "utils/AuthErrorMessages";
import { SignUpFieldErrors } from "utils/SignUpRules";

/**
 * Processes user signup by handling more complex scenarios, such as
 * server response validation and detailed error handling.
 * 
 * @async
 * @function processSignUpUser
 * @param {EndpointProps & { data: SignUpRequest }} params - The request parameters containing a unique tag and signup payload.
 * @returns {Promise<{ data: SubscriptionSetupData } | { error: string | null }}> - A promise that resolves to an object containing the signup response data or an error message.
 */
export async function processSignUpUser({
    tag,
    data,
}: EndpointProps & { data: SignUpRequest }): Promise<{ data: SubscriptionSetupData } | { error: string | null; fields?: SignUpFieldErrors }> {
    if (!data) {
        return {
            error: "Please provide all required details to create an account.",
        };
    }

    try {
        const result = await endpoints.signUpUser({ tag, data });

        if (result.error) {
            logError("Error creating user account.", { result }, tag);

            if (typeof result.error === "string") {
                // Say what is wrong, and where, in plain words (utils/AuthErrorMessages.ts).
                const friendly = friendlySignUpError({ serverMessage: result.error, serverDetail: result.detail ?? null });
                return friendly.fields ? { error: friendly.message, fields: friendly.fields } : { error: friendly.message };
            }

            return {
                error: "An unexpected error occurred while creating your account. Please try again later.",
            };
        }

        const defaultResponse: SubscriptionSetupData = {} as SubscriptionSetupData
        const formattedResponse: SubscriptionSetupData = (result.data as unknown as SubscriptionSetupData) ?? defaultResponse

        return { data: formattedResponse }
    } catch (error) {
        logError(
            "Unexpected error during signup.",
            { error: error instanceof Error ? error.message : "Unknown error" },
            tag
        );
        return {
            error: "We are unable to process your signup at the moment. Please check your connection and try again later.",
        };
    }
}

export async function processLoginUser({
    tag,
    data
}: EndpointProps & { data: { email: string; password: string; } }): Promise< { success: boolean; mustChangePassword?: boolean } | { error: string | null }> {
    if (!data) {
        return {
            error: "Please provide all required details to log in to your account."
        }
    }

    try {
        const result = await endpoints.logInUser({ tag, data })

        if (result.error) {
            logError('Error authenticating account.', { result }, tag)

            if (typeof result.error === 'string') {
                return {
                    error: friendlyLoginError({ serverMessage: result.error, serverDetail: result.detail ?? null })
                }
            }

            return {
                error: 'An unexpected error occurred while authenticating your account. Please try again later.'
            }
        }

        return {
            success: result.success,
            mustChangePassword: result.mustChangePassword ?? false
        }

    } catch (error) {
        logError(
            'Unexpected error during login.',
            { error: error instanceof Error ? error.message : 'Unknown error' },
            tag
        )
        return {
            error: 'We are unable to process your login request at the moment. Please check your connection and try again later.'
        }
    }
}

/* eslint-enable @typescript-eslint/no-redundant-type-constituents */
/* eslint-enable @typescript-eslint/no-unsafe-return */
/* eslint-enable @typescript-eslint/no-unsafe-argument */